/**
 * The pointers on the photograph: two or three short lines about the story, set on the plate.
 *
 * They come from the deep dive's `keyFacts`. Those facts still exist, but for three weeks they
 * have carried the label "Reported fact 1" to "Reported fact 4": the dive generator's source-only
 * fallback took over completely (1% of dives in week 29, 96% by week 31, 100% since) and it files
 * whole sentences under a placeholder label. The plate answered that by testing every fact for
 * "reads like data", short and carrying a digit, which no sentence passes, so the pointers
 * vanished entirely.
 *
 * The sentences were never the problem. The placeholder LABEL under them was. So a pointer is
 * shown with its label only when the label is a real one, and shown on its own when it is not,
 * which is what a reader wants in both cases. The generator is fixed too, so authored facts return
 * over time; nothing here waits on that.
 *
 * Nothing is composed. Every pointer is text the story already carries, trimmed at a word
 * boundary, and one that merely restates the headline sitting beside it is dropped.
 */

export type PlatePointer = { text: string; label?: string };

/** Labels the generator invents when it has none. A placeholder is worse than no label. */
const PLACEHOLDER_LABEL = /^(reported fact|fact|key fact|point|detail|item)\s*\d*$/i;

/** Is this label a placeholder the generator wrote because it had nothing to say? */
export function isPlaceholderLabel(label: unknown): boolean {
  return PLACEHOLDER_LABEL.test(String(label ?? "").trim());
}

export type DisplayFact = { value: string; label?: string; icon?: string; hint?: string };

/**
 * How a card's key facts should be SET, which is a different question from what they say.
 *
 * "figures" is the fact strip this feature was designed for: a sum or a count in a display size
 * with a small label under it, laid out as a grid. "lines" is what the generator's source-only
 * fallback actually produces: whole sentences under the placeholder label "Reported fact 1". Set
 * as figures those are unreadable, a sentence at 18px in a 142px column with REPORTED FACT 1
 * beneath it in gold caps, which is what readers have been shown for three weeks.
 *
 * One decision for the whole card, because a strip that mixes the two shapes reads as broken.
 */
export function displayFacts(
  facts: Array<{ value?: unknown; label?: unknown; icon?: unknown; hint?: unknown }> | undefined,
): { mode: "figures" | "lines"; facts: DisplayFact[] } {
  const kept = (facts ?? [])
    .map((f) => ({
      value: String(f?.value ?? "").trim(),
      label: String(f?.label ?? "").trim(),
      icon: f?.icon ? String(f.icon) : undefined,
      hint: f?.hint ? String(f.hint).trim() : undefined,
    }))
    .filter((f) => f.value);
  if (!kept.length) return { mode: "figures", facts: [] };
  const anyReal = kept.some((f) => f.label && !isPlaceholderLabel(f.label));
  if (anyReal) {
    return {
      mode: "figures",
      facts: kept.map((f) => ({
        value: f.value,
        label: isPlaceholderLabel(f.label) ? undefined : f.label || undefined,
        icon: f.icon,
        hint: f.hint,
      })),
    };
  }
  // Every label is a placeholder, so these are the fallback's sentences. Drop the labels
  // entirely and let the caller set them as lines.
  return { mode: "lines", facts: kept.map((f) => ({ value: f.value, hint: f.hint })) };
}

/** The longest a pointer may run before it is trimmed back to a word boundary. */
const MAX_POINTER = 104;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Trim to a word boundary, never mid-word, and never leave dangling punctuation. */
function clip(text: string, max = MAX_POINTER): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const at = cut.lastIndexOf(" ");
  const kept = (at > max * 0.5 ? cut.slice(0, at) : cut).replace(/[\s,;:.\-]+$/, "");
  // Say that it was cut. A line that simply stops mid-thought reads as a bug; three dots read as
  // a pointer into a longer sentence, which is exactly what it is.
  return `${kept}...`;
}

/** Does this pointer just say the headline again, 40px from the headline? */
function echoesHeadline(text: string, headline: string): boolean {
  const a = norm(text);
  const b = norm(headline);
  if (!a || !b) return false;
  return a.startsWith(b.slice(0, 40)) || b.startsWith(a.slice(0, 40));
}

/** A fact that reads like DATA earns its label: short, and carrying a digit. */
export function readsLikeData(fact: { label?: unknown; value?: unknown }): boolean {
  const value = String(fact?.value ?? "");
  const label = String(fact?.label ?? "");
  return value.length > 0 && value.length <= 28 && label.length <= 32 && /\d/.test(value);
}

/** Split reported copy into the sentences a pointer can be cut from. */
export function sentencesOf(text: string): string[] {
  return String(text ?? "")
    .split(/\r?\n|(?<=[.!?])\s+/)
    .map((s) => s.replace(/^[\s*\-•]+/, "").trim())
    .filter((s) => s.length >= 25);
}

/**
 * The pointers for one card. Prefers the dive's own facts, falls back to the card's reported
 * copy so a card without a usable dive still carries pointers, and returns an empty list only
 * when there is genuinely nothing short and specific to show.
 */
export function platePointers(
  card: { body?: string; essence?: string; headline?: string },
  facts: Array<{ label?: unknown; value?: unknown }> | undefined,
  limit = 3,
): PlatePointer[] {
  const headline = String(card.headline ?? "");
  const out: PlatePointer[] = [];
  const seen = new Set<string>();

  const add = (rawText: string, rawLabel?: string) => {
    if (out.length >= limit) return;
    const text = clip(rawText);
    if (text.length < 18) return;
    if (echoesHeadline(text, headline)) return;
    const key = norm(text).slice(0, 60);
    if (!key || seen.has(key)) return;
    seen.add(key);
    // A label is shown only when it is a real one AND the value is short enough to sit under it.
    const label =
      rawLabel && !PLACEHOLDER_LABEL.test(rawLabel.trim()) && rawLabel.trim().length <= 32
        ? rawLabel.trim()
        : undefined;
    out.push(label ? { text, label } : { text });
  };

  // An AUTHORED fact carries a real label, and its value was written to be short. A fallback fact
  // carries a placeholder label, and its value is the story's own sentence already clipped to 60
  // characters upstream, which is why those pointers used to stop mid-thought. So when the labels
  // are placeholders the dive is ignored and the pointers are cut from the card's own copy, where
  // the sentences are whole.
  const authored = (facts ?? []).filter(
    (f) => String(f?.value ?? "").trim() && !PLACEHOLDER_LABEL.test(String(f?.label ?? "").trim()),
  );
  if (authored.length) {
    for (const fact of authored) add(String(fact.value ?? ""), String(fact.label ?? ""));
  }
  if (out.length < limit) {
    for (const sentence of [...sentencesOf(card.body ?? ""), ...sentencesOf(card.essence ?? "")]) {
      add(sentence);
    }
  }
  return out;
}
